import { create } from 'xmlbuilder2'

import type { Company } from '../../api/services/company/types'
import type { InvoiceCompany } from '../../api/services/company/types'
import type { Invoice } from '../../api/services/invoice/types'
import { address } from './helpers.ts'

function companyAddressXml(parent: any, company: Company | InvoiceCompany) {
  parent
    .ele('etd:KodKraju')
    .txt('PL')
    .up()
    .ele('etd:Wojewodztwo')
    .txt('Wielkopolskie')
    .up()
    .ele('etd:Powiat')
    .txt('Powiat ostrowski')
    .up()
    .ele('etd:Gmina')
    .txt('Ostrów Wielkopolski')
    .up()
    .ele('etd:Ulica')
    .txt(company.address1 || '')
    .up()
    .ele('etd:NrDomu')
    .txt(company.building || '')
    .up()
    .ele('etd:Miejscowosc')
    .txt(company.city || '')
    .up()
    .ele('etd:KodPocztowy')
    .txt(company.postal_code || '')
    .up()
}

// FUNKCJA DO WERSJI 4!
export function generateJpkFaXml(
  invoices: Invoice[],
  company: Company,
  fromDate: string,
  toDate: string
): string {
  // Przestrzeń nazw i schemaLocation JPK_FA(4)
  const root = create({ version: '1.0', encoding: 'UTF-8' }).ele('tns:JPK', {
    'xmlns:tns': 'http://jpk.mf.gov.pl/wzor/2022/02/17/02171/',
    'xmlns:etd': 'http://crd.gov.pl/xml/schematy/dziedzinowe/mf/2018/08/24/eD/DefinicjeTypy/'
  })

  // Nagłówek zgodnie z FA(4)
  root
    .ele('tns:Naglowek')
    .ele('tns:KodFormularza', { kodSystemowy: 'JPK_FA (4)', wersjaSchemy: '1-0' })
    .txt('JPK_FA')
    .up()
    .ele('tns:WariantFormularza')
    .txt('4')
    .up()
    .ele('tns:CelZlozenia')
    .txt('1')
    .up()
    .ele('tns:DataWytworzeniaJPK')
    .txt(new Date().toISOString())
    .up()
    .ele('tns:DataOd')
    .txt(fromDate)
    .up()
    .ele('tns:DataDo')
    .txt(toDate)
    .up()
    .ele('tns:KodUrzedu')
    .txt('3017')
    .up()
    .up()

  // Podmiot1 (dane twojej firmy)
  const podmiot = root.ele('tns:Podmiot1')

  // IdentyfikatorPodmiotu (dla firmy z NIP)
  const idPodm = podmiot.ele('tns:IdentyfikatorPodmiotu')
  idPodm.ele('tns:NIP').txt(company.nip).up()
  idPodm.ele('tns:PelnaNazwa').txt(company.company_name).up()
  idPodm.up()

  const adresPodmiotu = podmiot.ele('tns:AdresPodmiotu')
  companyAddressXml(adresPodmiotu, company)
  podmiot.up()


  // Zmiennie do sumowania do FakturaCtrl
  let liczbaFaktur = 0
  let sumaFaktur = 0

  let liczbaWierszyFaktur = 0
  let wartoscWierszyFaktur = 0

  // Faktury i ich sekcje kontrolne
  invoices.forEach((invoice) => {
    const recipient = invoice.recipient_company_data as InvoiceCompany

    // Sekcja Faktura
    const faktura = root.ele('tns:Faktura')
    faktura.ele('tns:KodWaluty').txt('PLN')
    faktura.ele('tns:P_1').txt(invoice.created_at.substring(0, 10))
    faktura.ele('tns:P_2A').txt(invoice.invoice_number)
    faktura.ele('tns:P_3A').txt(recipient.name)
    recipient.address1 ? faktura.ele('tns:P_3B').txt(recipient.address1) : null
    faktura.ele('tns:P_3C').txt(invoice.issuing_company_data.name)
    faktura.ele('tns:P_3D').txt(address(company))
    faktura.ele('tns:P_4A').txt('PL')
    faktura.ele('tns:P_4B').txt(company.nip)
    faktura.ele('tns:P_5B').txt(recipient.nip)
    faktura.ele('tns:P_6').txt(invoice.sales_date.substring(0, 10))

    faktura.ele('tns:P_13_1').txt(
      invoice.positions.reduce((sum, item) => {
        const unitNet = (Number(item.unit_price) / Number(1 + item.vat_rate / 100)).toFixed(2)
        const totalNet = (Number(unitNet) * item.quantity).toFixed(2)
        return sum + Number(totalNet)
      }, 0).toFixed(2)
    )

    faktura.ele('tns:P_14_1').txt(
      invoice.positions.reduce((sum, item) => {
        const unitNet = (Number(item.unit_price) / Number(1 + item.vat_rate / 100)).toFixed(2)
        const totalNet = (Number(unitNet) * item.quantity).toFixed(2)
        const totalVAT = (Number(item.total_price) - Number(totalNet)).toFixed(2)
        return sum + Number(totalVAT)
      }, 0).toFixed(2)
    )

    faktura.ele('tns:P_13_2').txt('0.00')
    faktura.ele('tns:P_14_2').txt('0.00')
    faktura.ele('tns:P_13_3').txt('0.00')
    faktura.ele('tns:P_14_3').txt('0.00')
    faktura.ele('tns:P_13_4').txt('0.00')
    faktura.ele('tns:P_14_4').txt('0.00')
    faktura.ele('tns:P_13_5').txt('0.00')
    faktura.ele('tns:P_14_5').txt('0.00')
    faktura.ele('tns:P_13_6').txt('0.00')
    faktura.ele('tns:P_13_7').txt('0.00')

    faktura.ele('tns:P_15').txt(
      invoice.positions.reduce((sum, item) => {
        return sum + Number(item.total_price)
      }, 0).toFixed(2)
    )

    faktura.ele('tns:P_16').txt('false')
    faktura.ele('tns:P_17').txt('false')
    faktura.ele('tns:P_18').txt('false')
    faktura.ele('tns:P_18A').txt('false')
    faktura.ele('tns:P_19').txt('false')
    faktura.ele('tns:P_20').txt('false')
    faktura.ele('tns:P_20A').txt('false')
    faktura.ele('tns:P_20B').txt('false')
    faktura.ele('tns:P_21').txt('false')
    faktura.ele('tns:P_22').txt('false')
    faktura.ele('tns:P_23').txt('false')
    faktura.ele('tns:P_106E_2').txt('false')
    faktura.ele('tns:P_106E_3').txt('false')
    faktura.ele('tns:RodzajFaktury').txt('VAT')


    // Zbieraj dane do FakturaCtrl (sumarycznie)
    liczbaFaktur += invoice.positions.length
    sumaFaktur += invoice.positions
      .reduce((sum, pos) => sum + Number(pos.total_price || 0), 0)
  })

  // Po zakończeniu generowania wszystkich faktur i pozycji - generuj FakturaCtrl
  const fakturaCtrl = root.ele('tns:FakturaCtrl')
  fakturaCtrl.ele('tns:LiczbaFaktur').txt(liczbaFaktur.toString())
  fakturaCtrl.ele('tns:WartoscFaktur').txt(sumaFaktur.toFixed(2))

  // Pozycje faktury - sekcje FakturaWiersz i FakturaWierszCtrl
  invoices.forEach((invoice) => {
    invoice.positions.forEach((pos) => {
      const unitNet = (Number(pos.unit_price) / Number(1 + pos.vat_rate / 100)).toFixed(2)
      const totalNet = (Number(unitNet) * pos.quantity).toFixed(2)

      // Sekcja FakturaWiersz
      const poz = root.ele('tns:FakturaWiersz')
      poz.ele('tns:P_2B').txt(invoice.invoice_number)
      poz.ele('tns:P_7').txt(pos.description ?? '')
      poz.ele('tns:P_8A').txt(pos.unit ?? 'szt.')
      poz.ele('tns:P_8B').txt(pos.quantity?.toString() ?? '1')
      poz.ele('tns:P_9A').txt(unitNet ?? '1')
      poz.ele('tns:P_9B').txt(Number(pos.unit_price).toFixed(2) ?? '1')
      poz.ele('tns:P_11').txt(Number(totalNet).toFixed(2))
      poz.ele('tns:P_11A').txt(Number(pos.total_price).toFixed(2))
      poz.ele('tns:P_12').txt(Number(pos.vat_rate).toFixed(0))

      liczbaWierszyFaktur += 1
      wartoscWierszyFaktur += Number(totalNet)
    })
  })

      //   // Sekcja FakturaWierszCtrl
      const pozCtrl = root.ele('tns:FakturaWierszCtrl')
      pozCtrl.ele('tns:LiczbaWierszyFaktur').txt(liczbaWierszyFaktur.toFixed(0))
      pozCtrl.ele('tns:WartoscWierszyFaktur').txt(wartoscWierszyFaktur.toFixed(2))

  return root.end({ prettyPrint: true })
}
